import React from 'react';
const { useState, useRef, useEffect, useId } = React;
import { searchTimeZones, timeZoneOption, timeZonePreview } from '../../timeZones.js';

// A searchable time zone picker: people search by country or city ("Bolivia",
// "La Paz", "Madrid") and Oike stores the IANA id ("America/La_Paz"). It shows
// the local time before saving. An invalid stored value is flagged and never
// replaced by a guess: the person picks the right one. Works in the app and in
// the participant portal (colors inherit from the page).

const box = { width: '100%', boxSizing: 'border-box', padding: '8px 12px', borderRadius: 7, border: '1px solid var(--globant-border, rgba(127,127,127,0.45))', background: 'transparent', color: 'inherit', font: 'inherit', fontSize: 13 };

export default function TimeZoneSelect({ value, onChange, label = 'Zona horaria', compact = false }) {
  const current = timeZoneOption(value);
  const [open, setOpen] = useState(!current);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const listId = useId();
  const inputRef = useRef(null);
  const results = searchTimeZones(query, 40);
  const preview = current ? timeZonePreview(current.id) : null;
  useEffect(() => { if (open && !compact) inputRef.current?.focus?.(); }, [open]);
  useEffect(() => { setActive(0); }, [query]);
  const pick = (o) => { onChange(o.id); setOpen(false); setQuery(''); };
  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(a + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter' && results[active]) { e.preventDefault(); pick(results[active]); }
    else if (e.key === 'Escape' && current) { setOpen(false); setQuery(''); }
  };
  return (
    <div style={{ display: 'grid', gap: 6, minWidth: 0 }}>
      {value && !current && (
        <div role="alert" style={{ fontSize: 12, color: '#9A6212' }}>
          La zona guardada “{String(value).slice(0, 60)}” no es válida. Búscala por país o ciudad y elige la correcta: no se cambia sola.
        </div>
      )}
      {current && !open && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 13 }}>
          <b>{current.label}</b>
          <span style={{ opacity: 0.7 }}>{preview.time} ahora · {preview.offset}{preview.seasonal ? ' · cambia de horario en el año' : ''}</span>
          <button type="button" className="action-btn btn small" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => setOpen(true)}>Cambiar</button>
        </div>
      )}
      {open && (
        <div style={{ display: 'grid', gap: 4, minWidth: 0 }}>
          <input ref={inputRef} type="search" style={box} value={query} onChange={e => setQuery(e.target.value)} onKeyDown={onKey}
            placeholder="Busca un país o una ciudad: Bolivia, La Paz, Madrid…" aria-label={label} role="combobox" aria-expanded="true" aria-controls={listId}
            aria-activedescendant={results[active] ? `${listId}-${active}` : undefined} autoComplete="off" />
          <ul id={listId} role="listbox" aria-label={label} style={{ listStyle: 'none', margin: 0, padding: 0, maxHeight: 220, overflowY: 'auto', border: '1px solid var(--globant-border, rgba(127,127,127,0.3))', borderRadius: 7 }}>
            {!results.length && <li style={{ padding: '8px 12px', fontSize: 12, opacity: 0.7 }}>No hay zonas con ese nombre. Prueba con el país o la ciudad principal.</li>}
            {results.map((o, i) => {
              const p = timeZonePreview(o.id);
              return (
                <li key={o.id} id={`${listId}-${i}`} role="option" aria-selected={i === active} onMouseDown={e => { e.preventDefault(); pick(o); }} onMouseEnter={() => setActive(i)}
                  style={{ padding: '7px 12px', fontSize: 13, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', gap: 8, background: i === active ? 'rgba(91,191,181,0.15)' : 'transparent' }}>
                  <span>{o.label} <span style={{ opacity: 0.55, fontSize: 11 }}>{o.id}</span></span>
                  <span style={{ opacity: 0.7, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{p.time} · {p.offset}</span>
                </li>
              );
            })}
          </ul>
          {current && <button type="button" className="action-btn btn small" style={{ justifySelf: 'start', fontSize: 11, padding: '2px 8px' }} onClick={() => { setOpen(false); setQuery(''); }}>Cancelar</button>}
        </div>
      )}
    </div>
  );
}
